/**
 * Merge-tag interpolation for task titles and descriptions.
 *
 * Tokens are stored as {{namespace.field}} in the DB and resolved at
 * render time from whatever lead/user context the task already carries —
 * no extra API call needed.
 *
 * Unknown tokens are left as-is ({{lead.typo}} stays literal) so authors
 * can see and fix mistakes without data loss.
 */

// Tags available for insertion. Must stay in sync with buildContext() below.
export const TASK_MERGE_TAG_GROUPS = [
    {
        label: 'Lead',
        color: '#2563EB',
        tags: [
            { token: '{{lead.first_name}}', label: 'First name' },
            { token: '{{lead.last_name}}', label: 'Last name' },
            { token: '{{lead.full_name}}', label: 'Full name' },
            { token: '{{lead.email}}', label: 'Email' },
            { token: '{{lead.phone}}', label: 'Phone' },
            { token: '{{lead.treatment}}', label: 'Treatment' },
        ],
    },
    {
        label: 'Assignee',
        color: '#7C3AED',
        tags: [
            { token: '{{user.first_name}}', label: 'Assignee first name' },
            { token: '{{user.last_name}}', label: 'Assignee last name' },
            { token: '{{user.full_name}}', label: 'Assignee full name' },
        ],
    },
    {
        label: 'Practice',
        color: '#059669',
        tags: [
            { token: '{{practice.name}}', label: 'Practice name' },
        ],
    },
];

/**
 * Build a flat token→value map from whatever context is available.
 * All values coerce to string; missing values become '' so the token
 * is replaced with empty string rather than the literal token text.
 */
function buildContext(task, practice) {
    const lead = task?.lead || null;
    const latestTreatment = (() => {
        const ts = lead?.lead_treatments;
        if (!Array.isArray(ts) || ts.length === 0) return null;
        return [...ts].sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))[0];
    })();

    const primaryAssignee = Array.isArray(task?.assignees) && task.assignees.length > 0
        ? task.assignees[0]
        : null;
    const assigneeUser = primaryAssignee?.user || primaryAssignee || null;

    const firstName = lead?.first_name || '';
    const lastName = lead?.last_name || '';

    const assigneeFirst = assigneeUser?.first_name || '';
    const assigneeLast = assigneeUser?.last_name || '';

    return {
        '{{lead.first_name}}': firstName,
        '{{lead.last_name}}': lastName,
        '{{lead.full_name}}': lead?.full_name || `${firstName} ${lastName}`.trim(),
        '{{lead.email}}': lead?.email || lead?.lead_contact?.email || '',
        '{{lead.phone}}': lead?.phone || lead?.lead_contact?.phone || '',
        '{{lead.treatment}}': latestTreatment?.treatment || '',
        '{{user.first_name}}': assigneeFirst,
        '{{user.last_name}}': assigneeLast,
        '{{user.full_name}}': assigneeUser?.get_full_name
            || `${assigneeFirst} ${assigneeLast}`.trim(),
        '{{practice.name}}': practice?.name || '',
    };
}

const TOKEN_RE = /\{\{[a-z_]+\.[a-z_]+\}\}/g;

/**
 * Replace {{namespace.field}} tokens in `text` using context extracted
 * from `task` (and optionally `practice`). Unknown tokens are left as-is.
 */
export function interpolateTaskMergeTags(text, task, practice) {
    if (!text || typeof text !== 'string') return text || '';
    if (!TOKEN_RE.test(text)) return text;
    TOKEN_RE.lastIndex = 0;

    const ctx = buildContext(task, practice);
    return text.replace(TOKEN_RE, (match) => {
        if (Object.prototype.hasOwnProperty.call(ctx, match)) return ctx[match];
        return match; // unknown token — leave literal
    });
}

/**
 * Quick check: does `text` contain any merge-tag tokens?
 */
export function hasMergeTags(text) {
    if (!text || typeof text !== 'string') return false;
    TOKEN_RE.lastIndex = 0;
    return TOKEN_RE.test(text);
}
